import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { 
  ChakraProvider, 
  Box, 
  Grid, 
  Text, 
  Container, 
  Input,
  Button,
  useToast,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  useDisclosure,
  Card,
  CardBody,
  Heading,
  Divider,
  Flex,
  InputGroup,
  InputRightElement,
  IconButton,
  Tooltip,
  Badge,
  Spinner,
  Tabs,
  TabList,
  TabPanels,
  TabPanel,
  Tab,
  Table,
  Tbody,
  Tr,
  Td,
  Progress,
  Stack,
  Select,
  VStack,
  HStack,
  Tag,
  TagLabel,
  TagLeftIcon,
  SimpleGrid,
  TableContainer,
  Thead,
  Th,
  Alert,
  AlertIcon,
  List,
  ListItem,
  ListIcon,
  Link,
} from '@chakra-ui/react';
import { 
  FaKey, 
  FaSearch, 
  FaShieldAlt, 
  FaLock, 
  FaDownload, 
  FaChartLine, 
  FaGlobe, 
  FaExchangeAlt,
  FaStar,
  FaRegStar,
  FaHistory,
  FaCheckCircle,
  FaRocket,
  FaCog,
  FaLightbulb,
  FaChartBar,
  FaArrowRight,
  FaThumbsUp,
  FaThumbsDown,
  FaExclamationTriangle,
  FaBullseye,
  FaCubes,
  FaTrophy,
  FaTools,
  FaChartPie,
  FaChartArea,
} from 'react-icons/fa';
import OpenAI from 'openai';
import { theme } from './theme';
import companiesData from './data/companies.json';
import NewsFeed from './components/NewsFeed';
import HelpSection from './components/HelpSection';
import AnalyticsDashboard from './components/AnalyticsDashboard';
import FrameworkMappings from './components/FrameworkMappings';

const filterCategories = (categories, searchTerm) => {
  return Object.entries(categories).filter(([_, category]) => {
    const categoryMatches = category.name.toLowerCase().includes(searchTerm.toLowerCase());
    const companyMatches = category.companies.some(company => 
      company.toLowerCase().includes(searchTerm.toLowerCase())
    );
    return categoryMatches || companyMatches;
  });
};

const CompanyCard = React.memo(({ category, searchTerm, onCompanyClick, favorites, analysisCache }) => {
  return (
    <Card>
      <CardBody>
        <Flex align="center" mb={4} gap={2}>
          <FaLock />
          <Heading size="md">
            {category.name}
          </Heading>
        </Flex>
        <Divider mb={4} />
        <Flex wrap="wrap" gap={2}>
          {category.companies.filter(company =>
            company.toLowerCase().includes(searchTerm.toLowerCase())
          ).map((company, index) => (
            <Tooltip 
              key={`${company}-${index}`} 
              label={
                analysisCache[company] 
                  ? "Click to view cached analysis" 
                  : "Click for detailed analysis"
              } 
              placement="top"
            >
              <Badge
                px={3}
                py={1}
                cursor="pointer"
                onClick={() => onCompanyClick(company)}
                display="flex"
                alignItems="center"
                gap={1}
              >
                {analysisCache[company] && <FaHistory size="0.8em" />}
                {company}
                {favorites.includes(company) && (
                  <Box as="span" ml={1} color="brand.primary">
                    ★
                  </Box>
                )}
              </Badge>
            </Tooltip>
          ))}
        </Flex>
      </CardBody>
    </Card>
  );
});

function App() {
  const [apiKey, setApiKey] = useState(localStorage.getItem('openai_api_key') || '');
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [companyInfo, setCompanyInfo] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [comparisonMode, setComparisonMode] = useState(false);
  const [compareCompany, setCompareCompany] = useState(null);
  const [compareInfo, setCompareInfo] = useState('');
  const [marketData, setMarketData] = useState({});
  const [activeTab, setActiveTab] = useState('companies');
  const [favorites, setFavorites] = useState(() => {
    const saved = localStorage.getItem('favorites');
    return saved ? JSON.parse(saved) : [];
  });
  const [analysisCache, setAnalysisCache] = useState(() => {
    const saved = localStorage.getItem('analysisCache');
    return saved ? JSON.parse(saved) : {};
  });
  const toast = useToast();

  useEffect(() => {
    localStorage.setItem('analysisCache', JSON.stringify(analysisCache));
  }, [analysisCache]);

  useEffect(() => {
    localStorage.setItem('favorites', JSON.stringify(favorites));
  }, [favorites]);

  const validateApiKey = useCallback((key) => {
    return key.startsWith('sk-') && key.length > 20;
  }, []);

  const handleApiKeyChange = useCallback((e) => {
    const newKey = e.target.value;
    setApiKey(newKey);
    
    if (validateApiKey(newKey)) {
      localStorage.setItem('openai_api_key', newKey);
      toast({
        title: 'API Key Saved',
        description: 'Your OpenAI API key has been securely saved',
        status: 'success',
        duration: 2000,
        position: 'top',
      });
    }
  }, [validateApiKey, toast]);

  const toggleFavorite = useCallback((company) => {
    setFavorites(prev => {
      if (prev.includes(company)) {
        return prev.filter(c => c !== company);
      }
      return [...prev, company];
    });
  }, []);

  const fetchCompanyInfo = useCallback(async (company) => {
    if (!validateApiKey(apiKey)) {
      toast({
        title: 'Invalid API Key',
        description: 'Please enter a valid OpenAI API key',
        status: 'error',
        duration: 3000,
        position: 'top',
      });
      return;
    }

    setSelectedCompany(company);
    onOpen();

    if (analysisCache[company]) {
      setCompanyInfo(analysisCache[company].analysis);
      setMarketData(analysisCache[company].marketData || {});
      return;
    }

    setIsLoading(true);

    try {
      const openai = new OpenAI({
        apiKey: apiKey,
        dangerouslyAllowBrowser: true
      });

      const response = await openai.chat.completions.create({
        model: "gpt-5-nano",
        messages: [{
          role: "user",
          content: `You are analyzing the company "${company}". 

          CRITICAL INSTRUCTIONS:
          - If you have reliable, specific information about this company, provide a detailed analysis
          - If you are uncertain or lack specific information, clearly state: "I have limited information about ${company}. The following analysis is based on available data and may not be complete."
          - Do NOT fabricate specific details like exact revenue figures, market share percentages, or recent developments unless you're confident in the information
          - Focus on what you actually know rather than making assumptions

          Please provide a detailed report in the following format:

          # Executive Summary
          [Brief 2-3 sentence overview of the company]

          ## Core Products & Services 🛡️
          - [Key product 1]
              • [Feature/benefit]
              • [Feature/benefit]
          - [Key product 2]
              • [Feature/benefit]
              • [Feature/benefit]

          ## Key Strengths 💪
          - [Strength 1 with brief explanation]
          - [Strength 2 with brief explanation]
          - [Strength 3 with brief explanation]

          ## Market Position 📊
          - Market Share: [Estimate]
          - Target Segments: [List primary customer segments]
          - Competitive Standing: [Brief analysis]

          ## Notable Innovations 🚀
          - [Innovation 1 with impact]
          - [Innovation 2 with impact]
          - [Innovation 3 with impact]

          ## Recent Developments 📈
          - [Latest development 1]
          - [Latest development 2]
          - [Latest development 3]

          ## SWOT Analysis
          
          **Strengths**
          - [Point 1]
          - [Point 2]

          **Weaknesses**
          - [Point 1]
          - [Point 2]

          **Opportunities**
          - [Point 1]
          - [Point 2]

          **Threats**
          - [Point 1]
          - [Point 2]

          ## Future Outlook 🔮
          [3-4 sentences about future prospects and potential growth areas]

          ## Market Metrics (JSON)
          Please provide the following market metrics in JSON format within a code block:
          \`\`\`json
          {
            "marketShare": [estimated market share percentage as number],
            "growth": [estimated year-over-year growth percentage as number],
            "competitors": [estimated number of direct competitors as integer],
            "yearlyRevenue": [estimated annual revenue in millions USD as integer]
          }
          \`\`\`
        `}],

      });

      const analysis = response.choices[0].message.content;
      
      // Extract JSON market data from the response
      const jsonMatch = analysis.match(/```json\s*(\{[\s\S]*?\})\s*```/);
      let marketData = {};
      let cleanedAnalysis = analysis;
      
      if (jsonMatch) {
        try {
          marketData = JSON.parse(jsonMatch[1]);
          // Remove the JSON block from the analysis text
          cleanedAnalysis = analysis.replace(/## Market Metrics \(JSON\)[\s\S]*?```json[\s\S]*?```/g, '').trim();
        } catch (error) {
          console.warn('Failed to parse market data JSON:', error);
          // Fallback to default values if JSON parsing fails
          marketData = {
            marketShare: 0,
            growth: 0,
            competitors: 0,
            yearlyRevenue: 0
          };
        }
      }
      
      setCompanyInfo(cleanedAnalysis);
      setMarketData(marketData);
      setAnalysisCache(prev => ({
        ...prev,
        [company]: {
          analysis: cleanedAnalysis,
          marketData: marketData
        }
      }));

    } catch (error) {
      console.error('Analysis error:', error);
      toast({
        title: 'Analysis Failed',
        description: `Failed to analyze ${company}. Please try again. Error: ${error.message}`,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      setCompanyInfo('Analysis failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }, [apiKey, validateApiKey, toast, onOpen, analysisCache]);

  const compareCompanies = useCallback(async () => {
    if (!compareCompany) return;
    if (!validateApiKey(apiKey)) {
      toast({
        title: 'Invalid API Key',
        description: 'Please enter a valid OpenAI API key',
        status: 'error',
        duration: 3000,
        position: 'top',
      });
      return;
    }
    setIsLoading(true);
    
    try {
      const openai = new OpenAI({
        apiKey: apiKey,
        dangerouslyAllowBrowser: true
      });

      const response = await openai.chat.completions.create({
        model: "gpt-4o-mini",
        messages: [{
          role: "user",
          content: `CRITICAL: You MUST provide EXACTLY EQUAL coverage for both companies. This is a BALANCED comparison.

          Company A: ${selectedCompany}
          Company B: ${compareCompany}

          MANDATORY STRUCTURE - You MUST fill out EVERY section for BOTH companies:

          # Executive Summary
          [Brief comparison mentioning BOTH companies equally - 2-3 sentences]

          ## Market Position 🎯
          ### ${selectedCompany}
          • [Market position point 1]
          • [Market position point 2] 
          • [Market position point 3]

          ### ${compareCompany}
          • [Market position point 1]
          • [Market position point 2]
          • [Market position point 3]

          ## Product Offerings 🛡️
          ### ${selectedCompany}
          • [Product 1 with description]
          • [Product 2 with description]
          • [Product 3 with description]

          ### ${compareCompany}
          • [Product 1 with description]
          • [Product 2 with description]
          • [Product 3 with description]

          ## Competitive Advantages 💪
          ### ${selectedCompany}
          • [Advantage 1]
          • [Advantage 2]
          • [Advantage 3]

          ### ${compareCompany}
          • [Advantage 1]
          • [Advantage 2]
          • [Advantage 3]

          ## Technology Stack 🔧
          ### ${selectedCompany}
          • [Core technologies]
          • [Key platforms]
          • [Technical differentiators]

          ### ${compareCompany}
          • [Core technologies]
          • [Key platforms]
          • [Technical differentiators]

          ## Future Outlook 🚀
          ### ${selectedCompany}
          • [Growth prospects]
          • [Upcoming initiatives]
          • [Market opportunities]

          ### ${compareCompany}
          • [Growth prospects]
          • [Upcoming initiatives]
          • [Market opportunities]

          ## SWOT Analysis

          ### ${selectedCompany}
          **Strengths:**
          • [Strength 1]
          • [Strength 2]
          • [Strength 3]

          **Weaknesses:**
          • [Weakness 1]
          • [Weakness 2]

          **Opportunities:**
          • [Opportunity 1]
          • [Opportunity 2]

          **Threats:**
          • [Threat 1]
          • [Threat 2]

          ### ${compareCompany}
          **Strengths:**
          • [Strength 1]
          • [Strength 2]
          • [Strength 3]

          **Weaknesses:**
          • [Weakness 1]
          • [Weakness 2]

          **Opportunities:**
          • [Opportunity 1]
          • [Opportunity 2]

          **Threats:**
          • [Threat 1]
          • [Threat 2]

          ## Head-to-Head Comparison 🥊
          ### Direct Competition Areas
          • [Area 1: How they compete]
          • [Area 2: How they compete]
          • [Area 3: How they compete]

          ### Key Differentiators
          • **${selectedCompany}:** [What makes them unique]
          • **${compareCompany}:** [What makes them unique]

          ### Winner in Each Category
          • **Market Position:** [Company] - [Brief reason]
          • **Product Portfolio:** [Company] - [Brief reason]
          • **Innovation:** [Company] - [Brief reason]
          • **Customer Value:** [Company] - [Brief reason]

          ## Strategic Recommendations
          ### ${selectedCompany}
          1. [Specific recommendation]
          2. [Specific recommendation]

          ### ${compareCompany}
          1. [Specific recommendation]
          2. [Specific recommendation]

          ## Market Metrics
          | Metric | ${selectedCompany} | ${compareCompany} |
          |--------|-------------------|-------------------|
          | Market Share | [Estimate] | [Estimate] |
          | YoY Growth | [Estimate] | [Estimate] |
          | Global Presence | [Score /10] | [Score /10] |
          | Innovation Index | [Score /10] | [Score /10] |

          CRITICAL REMINDER: You MUST provide equal detail for BOTH companies in EVERY section above. Do not skip any company's sections!`
        }],
      });

      const analysis = response.choices[0].message.content;
      setCompareInfo(analysis);
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message,
        status: 'error',
        duration: 5000,
      });
    } finally {
      setIsLoading(false);
    }
  }, [compareCompany, selectedCompany, apiKey, validateApiKey, toast]);

  const exportToHTML = useCallback(() => {
    const content = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>${selectedCompany} - Cybersecurity Analysis</title>
          <style>
            body { font-family: Arial, sans-serif; max-width: 800px; margin: 2rem auto; padding: 0 1rem; }
            h1 { color: #00994f; }
            .section { margin: 2rem 0; }
            .metrics { display: grid; grid-template-columns: repeat(2, 1fr); gap: 1rem; }
            .metric { background: #f5f5f5; padding: 1rem; border-radius: 8px; }
          </style>
        </head>
        <body>
          <h1>${selectedCompany} Analysis Report</h1>
          <div class="section">
            <h2>Company Overview</h2>
            ${companyInfo.replace(/\n/g, '<br>')}
          </div>
          <div class="section">
            <h2>Market Metrics</h2>
            <div class="metrics">
              <div class="metric">
                <h3>Market Share</h3>
                <p>${marketData.marketShare?.toFixed(1)}%</p>
              </div>
              <div class="metric">
                <h3>YoY Growth</h3>
                <p>${marketData.growth?.toFixed(1)}%</p>
              </div>
              <div class="metric">
                <h3>Direct Competitors</h3>
                <p>${marketData.competitors}</p>
              </div>
              <div class="metric">
                <h3>Annual Revenue</h3>
                <p>$${marketData.yearlyRevenue}M</p>
              </div>
            </div>
          </div>
          ${compareInfo ? `
            <div class="section">
              <h2>Comparison with ${compareCompany}</h2>
              ${compareInfo.replace(/\n/g, '<br>')}
            </div>
          ` : ''}
          <footer>
            <p>Generated by Neatlabs™ Cybersecurity Ecosystem Explorer on ${new Date().toLocaleDateString()}</p>
          </footer>
        </body>
      </html>
    `;

    const blob = new Blob([content], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${selectedCompany}-analysis.html`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, [selectedCompany, companyInfo, marketData, compareInfo, compareCompany]);

  const uniqueCompanies = useMemo(() => {
    const allCompanies = Object.values(companiesData.categories)
      .flatMap(cat => cat.companies)
      .filter(company => company !== selectedCompany);
    return [...new Set(allCompanies)].sort();
  }, [selectedCompany]);

  const filteredCategories = useMemo(() => {
    return filterCategories(companiesData.categories, searchTerm);
  }, [searchTerm]);

  return (
    <ChakraProvider theme={theme}>
      <Box minH="100vh" py={8} position="relative">
        <Container maxW="container.xl" position="relative" zIndex="1">
          <Flex justify="space-between" align="center" mb={8}>
            <Heading
              as="h1"
              size="2xl"
              display="flex"
              alignItems="center"
              gap={4}
            >
              <FaShieldAlt />
              Neatlabs™ Cyber & Digital Intelligence
            </Heading>
          </Flex>

          <Box maxW="md" w="100%" mb={8} mx="auto">
            <InputGroup size="lg" mb={4}>
              <Input
                placeholder="Enter your OpenAI API key"
                value={apiKey}
                onChange={handleApiKeyChange}
                type="password"
              />
              <InputRightElement>
                <IconButton
                  icon={<FaKey />}
                  variant="cyber"
                  size="sm"
                  onClick={() => {
                    if (validateApiKey(apiKey)) {
                      localStorage.setItem('openai_api_key', apiKey);
                      toast({
                        title: 'API Key Saved',
                        description: 'Your OpenAI API key has been securely saved',
                        status: 'success',
                        duration: 2000,
                        position: 'top',
                      });
                    } else {
                      toast({
                        title: 'Invalid API Key',
                        description: 'Please enter a valid OpenAI API key',
                        status: 'error',
                        duration: 3000,
                        position: 'top',
                      });
                    }
                  }}
                  aria-label="Save API Key"
                />
              </InputRightElement>
            </InputGroup>

            <InputGroup size="lg">
              <Input
                placeholder="Search companies or categories..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <InputRightElement>
                <FaSearch color="#4299E1" />
              </InputRightElement>
            </InputGroup>
          </Box>

          <Tabs variant="soft-rounded" mb={6} onChange={(index) => setActiveTab(index === 0 ? 'companies' : index === 1 ? 'analytics' : index === 2 ? 'news' : index === 3 ? 'frameworks' : 'help')}>
            <TabList>
              <Tab>Companies</Tab>
              <Tab>Analytics</Tab>
              <Tab>News Feed</Tab>
              <Tab>Frameworks</Tab>
              <Tab>Help</Tab>
            </TabList>

            <TabPanels>
              <TabPanel>
                <Grid templateColumns="repeat(auto-fit, minmax(300px, 1fr))" gap={6}>
                  {filteredCategories.map(([key, category]) => (
                    <CompanyCard
                      key={key}
                      category={category}
                      searchTerm={searchTerm}
                      onCompanyClick={fetchCompanyInfo}
                      favorites={favorites}
                      analysisCache={analysisCache}
                    />
                  ))}
                </Grid>
              </TabPanel>

              <TabPanel>
                <AnalyticsDashboard data={companiesData} />
              </TabPanel>

              <TabPanel>
                <NewsFeed apiKey={apiKey} />
              </TabPanel>

              <TabPanel>
                <FrameworkMappings />
              </TabPanel>

              <TabPanel>
                <HelpSection />
              </TabPanel>
            </TabPanels>
          </Tabs>

          <Modal isOpen={isOpen} onClose={onClose} size="xl">
            <ModalOverlay backdropFilter="blur(4px)" />
            <ModalContent>
              <ModalHeader display="flex" alignItems="center" gap={2}>
                <FaShieldAlt />
                {selectedCompany}
                <IconButton
                  icon={favorites.includes(selectedCompany) ? <FaStar /> : <FaRegStar />}
                  onClick={() => toggleFavorite(selectedCompany)}
                  variant="ghost"
                  aria-label="Toggle favorite"
                />
                {analysisCache[selectedCompany] && (
                  <Tooltip label="Using cached analysis">
                    <Box as="span">
                      <FaHistory />
                    </Box>
                  </Tooltip>
                )}
              </ModalHeader>
              <ModalCloseButton />
              <ModalBody pb={6}>
                <Tabs variant="soft-rounded">
                  <TabList mb={4}>
                    <Tab>Overview</Tab>
                    <Tab>Market Data</Tab>
                    <Tab>Compare</Tab>
                  </TabList>

                  <TabPanels>
                    <TabPanel>
                      {isLoading ? (
                        <Flex justify="center" align="center" minH="200px">
                          <Spinner size="xl" thickness="4px" />
                        </Flex>
                      ) : (
                        <Box className="company-analysis">
                          <VStack align="stretch" spacing={6}>
                            {companyInfo.split('\n\n').map((section, index) => {
                              if (section.startsWith('# ')) {
                                return (
                                  <Box key={index}>
                                    <Heading size="lg" mb={4}>
                                      {section.replace('# ', '')}
                                    </Heading>
                                  </Box>
                                );
                              } else if (section.startsWith('## ')) {
                                const title = section.split('\n')[0].replace('## ', '');
                                const content = section.split('\n').slice(1).join('\n');
                                const icon = {
                                  'Core Products & Services 🛡️': FaShieldAlt,
                                  'Key Strengths 💪': FaCheckCircle,
                                  'Market Position 📊': FaChartBar,
                                  'Notable Innovations 🚀': FaRocket,
                                  'Recent Developments 📈': FaChartLine,
                                  'SWOT Analysis': FaCog,
                                  'Future Outlook 🔮': FaLightbulb
                                }[title] || FaShieldAlt;

                                return (
                                  <Card key={index} variant="elevated">
                                    <CardBody>
                                      <VStack align="stretch" spacing={4}>
                                        <HStack>
                                          <Box as={icon} />
                                          <Heading size="md">
                                            {title}
                                          </Heading>
                                        </HStack>
                                        <Box pl={6}>
                                          {content.split('\n').map((line, i) => (
                                            <Text key={i} mb={2}>
                                              {line}
                                            </Text>
                                          ))}
                                        </Box>
                                      </VStack>
                                    </CardBody>
                                  </Card>
                                );
                              }
                              return null;
                            })}
                          </VStack>
                        </Box>
                      )}
                    </TabPanel>

                    <TabPanel>
                      <Stack spacing={4}>
                        <Box>
                          <Text mb={2}>Market Share</Text>
                          <Progress value={marketData.marketShare || 0} max={100} />
                          <Text>{marketData.marketShare ? `${marketData.marketShare.toFixed(1)}%` : 'Data not available'}</Text>
                        </Box>
                        <Box>
                          <Text mb={2}>YoY Growth</Text>
                          <Progress value={Math.abs(marketData.growth || 0)} max={100} colorScheme={marketData.growth >= 0 ? 'green' : 'red'} />
                          <Text>{marketData.growth ? `${marketData.growth.toFixed(1)}%` : 'Data not available'}</Text>
                        </Box>
                        <Table variant="simple">
                          <Tbody>
                            <Tr>
                              <Td>Direct Competitors</Td>
                              <Td isNumeric>{marketData.competitors || 'N/A'}</Td>
                            </Tr>
                            <Tr>
                              <Td>Annual Revenue (M$)</Td>
                              <Td isNumeric>{marketData.yearlyRevenue ? `$${marketData.yearlyRevenue}M` : 'N/A'}</Td>
                            </Tr>
                          </Tbody>
                        </Table>
                      </Stack>
                    </TabPanel>

                    <TabPanel>
                      <Stack spacing={4}>
                        <Select
                          placeholder="Select company to compare"
                          onChange={(e) => setCompareCompany(e.target.value)}
                        >
                          {uniqueCompanies.map((company, index) => (
                            <option key={`${company}-${index}`} value={company}>
                              {company}
                            </option>
                          ))}
                        </Select>
                        <Button
                          leftIcon={<FaExchangeAlt />}
                          variant="cyber"
                          onClick={compareCompanies}
                          isDisabled={!compareCompany}
                        >
                          Compare Companies
                        </Button>
                        {isLoading ? (
                          <Flex justify="center" py={8}>
                            <Spinner size="xl" />
                          </Flex>
                        ) : compareInfo && (
                          <Box>
                            {compareInfo.split('\n\n').map((section, index) => {
                              if (section.startsWith('# ')) {
                                return (
                                  <Heading size="lg" mb={4} key={index}>
                                    {section.replace('# ', '')}
                                  </Heading>
                                );
                              } else if (section.startsWith('## ')) {
                                const title = section.split('\n')[0].replace('## ', '');
                                const content = section.split('\n').slice(1).join('\n');
                                
                                const icon = {
                                  'Market Position 🎯': FaBullseye,
                                  'Product Offerings 🛡️': FaCubes,
                                  'Competitive Advantages 💪': FaTrophy,
                                  'Technology Stack 🔧': FaTools,
                                  'Future Outlook 🚀': FaRocket,
                                  'Market Metrics': FaChartPie,
                                  'SWOT Analysis': FaExchangeAlt,
                                  'Strategic Recommendations': FaLightbulb
                                }[title] || FaShieldAlt;

                                return (
                                  <Card key={index} mb={4}>
                                    <CardBody>
                                      <VStack align="stretch" spacing={4}>
                                        <HStack>
                                          <Box as={icon} />
                                          <Heading size="md">{title}</Heading>
                                        </HStack>
                                        <Box>
                                          {content.split('\n').map((line, i) => {
                                            if (line.startsWith('### ')) {
                                              return (
                                                <Heading size="sm" mt={4} mb={2} key={i}>
                                                  {line.replace('### ', '')}
                                                </Heading>
                                              );
                                            }
                                            if (line.startsWith('| ')) {
                                              if (i === 0) {
                                                return (
                                                  <TableContainer key={i}>
                                                    <Table variant="simple">
                                                      <Thead>
                                                        <Tr>
                                                          {line.split('|').filter(Boolean).map((cell, cellIndex) => (
                                                            <Th key={cellIndex}>{cell.trim()}</Th>
                                                          ))}
                                                        </Tr>
                                                      </Thead>
                                                      <Tbody>
                                                        {content.split('\n')
                                                          .filter(row => row.startsWith('|') && !row.includes('|-'))
                                                          .slice(1)
                                                          .map((row, rowIndex) =>
                                                            <Tr key={rowIndex}>
                                                              {row.split('|').filter(Boolean).map((cell, cellIndex) => (
                                                                <Td key={cellIndex}>{cell.trim()}</Td>
                                                              ))}
                                                            </Tr>
                                                          )}
                                                      </Tbody>
                                                    </Table>
                                                  </TableContainer>
                                                );
                                              }
                                              return null;
                                            }
                                            if (line.startsWith('Strengths:') || line.startsWith('Weaknesses:') || 
                                                line.startsWith('Opportunities:') || line.startsWith('Threats:')) {
                                              return (
                                                <Box key={i} mb={2}>
                                                  <Text fontWeight="bold" mb={1}>{line}</Text>
                                                </Box>
                                              );
                                            }
                                            if (line.startsWith('•')) {
                                              return (
                                                <HStack key={i} mb={2} ml={4}>
                                                  <Box as={FaArrowRight} size="12px" />
                                                  <Text>{line.replace('•', '').trim()}
                                                  </Text>
                                                </HStack>
                                              );
                                            }
                                            return <Text key={i} mb={2}>{line}</Text>;
                                          })}
                                        </Box>
                                      </VStack>
                                    </CardBody>
                                  </Card>
                                );
                              }
                              return null;
                            })}
                          </Box>
                        )}
                      </Stack>
                    </TabPanel>
                  </TabPanels>
                </Tabs>
              </ModalBody>
              <ModalFooter>
                <Button
                  leftIcon={<FaDownload />}
                  variant="cyber"
                  onClick={exportToHTML}
                  mr={3}
                >
                  Export Report
                </Button>
                <Button
                  leftIcon={<FaGlobe />}
                  variant="cyber"
                  as="a"
                  href={`https://www.google.com/search?q=${encodeURIComponent(selectedCompany + " cybersecurity")}`}
                  target="_blank"
                >
                  Visit Website
                </Button>
              </ModalFooter>
            </ModalContent>
          </Modal>

          <Box as="footer" mt={12} pt={6} borderTop="1px" borderColor="gray.200">
            <HStack spacing={4} justify="center">
              <Link 
                href="https://neatlabs.ai/policies/#terms"
                isExternal
                color="gray.600"
                fontSize="sm"
                _hover={{ color: 'brand.500', textDecoration: 'none' }}
              >
                Terms of Service
              </Link>
              <Text color="gray.400">•</Text>
              <Link
                href="https://neatlabs.ai/policies/#privacy"
                isExternal
                color="gray.600"
                fontSize="sm"
                _hover={{ color: 'brand.500', textDecoration: 'none' }}
              >
                Privacy Policy
              </Link>
              <Text color="gray.400">•</Text>
              <Link
                href="https://neatlabs.ai/policies/#cookies"
                isExternal
                color="gray.600"
                fontSize="sm"
                _hover={{ color: 'brand.500', textDecoration: 'none' }}
              >
                Cookie Policy
              </Link>
            </HStack>
          </Box>
        </Container>
      </Box>
    </ChakraProvider>
  );
}

export default App;